import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  Download as DownloadIcon,
  PhoneAndroid as AndroidIcon,
  Apple as AppleIcon,
  DesktopWindows as DesktopIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

interface DownloadAppProps {
  variant?: 'button' | 'icon';
  showText?: boolean;
}

const DownloadApp: React.FC<DownloadAppProps> = ({ variant = 'button', showText = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isMacOS, setIsMacOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isDesktopApp, setIsDesktopApp] = useState(false);

  useEffect(() => {
    // Check if user is on macOS
    const isMacOSDevice = /Mac/.test(navigator.userAgent);
    setIsMacOS(isMacOSDevice);
    
    // Check if the app is already installed (standalone mode)
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(isStandaloneMode);

    // Check if running in desktop app
    const isDesktopAppView = (window as any).electron?.isDesktopApp === true;
    setIsDesktopApp(isDesktopAppView);

    // Check if user is on iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    });
  }, []);

  // If running in standalone mode or desktop app, don't show the download button
  if (isStandalone || isDesktopApp) {
    return null;
  }

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  const handleDownload = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const renderDownloadButton = () => {
    if (variant === 'icon' && !showText) {
      return (
        <Tooltip title="Download App">
          <IconButton
            onClick={handleDownload}
            color="primary"
            size="large"
            sx={{
              backgroundColor: 'rgba(30, 136, 229, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(30, 136, 229, 0.2)',
              },
            }}
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      );
    }

    return (
      <Button
        variant="contained"
        startIcon={<DownloadIcon />}
        onClick={handleDownload}
        sx={{
          backgroundColor: '#1E88E5',
          '&:hover': {
            backgroundColor: '#1976D2',
          },
          textTransform: 'none',
          fontWeight: 500,
        }}
      >
        Download App
      </Button>
    );
  };

  return (
    <>
      {renderDownloadButton()}
      
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 3,
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Download Superior Payroll
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          <Grid container spacing={3} sx={{ p: 2 }}>
            {/* Desktop Option */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <DesktopIcon sx={{ fontSize: 48, color: '#1E88E5' }} />
                  </Box>
                  <Typography variant="h6" gutterBottom align="center">
                    Desktop App
                  </Typography>
                  {isMacOS ? (
                    <>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Currently available as a web app. Install on your Mac:
                      </Typography>
                      <ol style={{ paddingLeft: '1.2rem', margin: 0 }}>
                        <Typography variant="body2" component="li" color="text.secondary">
                          Open in Chrome or Edge browser
                        </Typography>
                        <Typography variant="body2" component="li" color="text.secondary">
                          Click the install icon in the address bar
                        </Typography>
                      </ol>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                        Native macOS app coming soon to the Mac App Store
                      </Typography>
                    </>
                  ) : !isIOS && deferredPrompt ? (
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Install Superior Payroll directly on your desktop for the best experience.
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Visit this website in Chrome or Edge to install the desktop app.
                    </Typography>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  {!isIOS && deferredPrompt ? (
                    <Button
                      variant="contained"
                      startIcon={<DesktopIcon />}
                      onClick={handleInstallClick}
                      fullWidth
                      sx={{ maxWidth: 200 }}
                    >
                      Install Now
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={<DesktopIcon />}
                      disabled
                      fullWidth
                      sx={{ maxWidth: 200 }}
                    >
                      {isMacOS ? 'Use Chrome/Edge' : 'Use Chrome/Edge'}
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>

            {/* iOS Option */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <AppleIcon sx={{ fontSize: 48, color: '#1E88E5' }} />
                  </Box>
                  <Typography variant="h6" gutterBottom align="center">
                    iOS App
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Currently available as a web app. Install on your iPhone or iPad:
                  </Typography>
                  <ol style={{ paddingLeft: '1.2rem', margin: 0 }}>
                    <Typography variant="body2" component="li" color="text.secondary">
                      Open in Safari browser
                    </Typography>
                    <Typography variant="body2" component="li" color="text.secondary">
                      Tap the Share button (box with arrow)
                    </Typography>
                    <Typography variant="body2" component="li" color="text.secondary">
                      Scroll down and tap "Add to Home Screen"
                    </Typography>
                  </ol>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Native iOS app coming soon to the App Store
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <Button
                    variant={isIOS ? 'contained' : 'outlined'}
                    startIcon={<AppleIcon />}
                    fullWidth
                    sx={{ maxWidth: 200 }}
                    disabled={!isIOS}
                  >
                    {isIOS ? 'Install Now' : 'Use Safari on iOS'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* Android Option */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <AndroidIcon sx={{ fontSize: 48, color: '#1E88E5' }} />
                  </Box>
                  <Typography variant="h6" gutterBottom align="center">
                    Android App
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Install on your Android device:
                  </Typography>
                  <ol style={{ paddingLeft: '1.2rem', margin: 0 }}>
                    <Typography variant="body2" component="li" color="text.secondary">
                      Open in Chrome
                    </Typography>
                    <Typography variant="body2" component="li" color="text.secondary">
                      Tap menu (⋮)
                    </Typography>
                    <Typography variant="body2" component="li" color="text.secondary">
                      Choose "Add to Home Screen"
                    </Typography>
                  </ol>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <Button
                    variant={!isIOS ? 'contained' : 'outlined'}
                    startIcon={<AndroidIcon />}
                    fullWidth
                    sx={{ maxWidth: 200 }}
                    disabled={isIOS}
                  >
                    {!isIOS ? 'Install Now' : 'Use Chrome on Android'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DownloadApp; 